import {
  displayNameComparator,
  getAggregationDrawerOptions,
  getCurrencyDrawerOptions,
  getCustomerDrawerOptions,
  getFeeModeDrawerOptions,
  getSubAccountDrawerOptions,
  MarketFeeModeEnum,
  type Aggregation,
  type ColumnDef,
  type Currency,
  type Customer,
  type CustomerSecurity,
  type InputsAndDropdownsDrawerOption,
  type PricingRule,
  type Security,
  type SubAccount,
} from '@talos/kyoko';
import type { ColDef } from 'ag-grid-community';
import { BOOLEAN_OPTIONS, EXCLUDE_EXCHANGE_FEES_GUIDE_TEXT } from '../utils';

export type CustomerPricingDrawerOptionsThroughSubAccountsParams = {
  pricingRulesByCounterpartyBySymbol: Map<string, Map<string | undefined, PricingRule>>;
  customers: Customer[];
  subAccounts: SubAccount[];
  customerSecurities: CustomerSecurity[];
  searchableSecurities: Security[];
};

/**
 * Counterparty, symbol (symbol selector), divider, trade + hedge subaccounts — shared with Customer SubAccount Mappings.
 */
export function getCustomerPricingDrawerOptionsThroughSubAccounts({
  pricingRulesByCounterpartyBySymbol,
  customers,
  subAccounts,
  customerSecurities,
  searchableSecurities,
}: CustomerPricingDrawerOptionsThroughSubAccountsParams): InputsAndDropdownsDrawerOption<PricingRule>[] {
  return [
    {
      field: 'Counterparty',
      type: 'dropdown',
      getDropdownOptions: () => getCustomerDrawerOptions(customers),
      disabledWhenEditing: true,
      getIsRequired: () => true,
    },
    {
      field: 'Symbol',
      type: 'symbolSelector',
      getAvailableSecurities: (form, _isEditing) => {
        if (!form.Counterparty) {
          return [];
        }
        const allowedSymbols = new Set(customerSecurities.map(cs => cs.Symbol));
        return searchableSecurities.filter(sec => allowedSymbols.has(sec.Symbol) || sec.Symbol === form.Symbol);
      },
      getIsItemDisabled: (security, form, _isEditing) => {
        if (!form.Counterparty || security.Symbol === form.Symbol) {
          return false;
        }
        const usedForCounterparty = pricingRulesByCounterpartyBySymbol.get(form.Counterparty);
        return usedForCounterparty?.has(security.Symbol) ?? false;
      },
      disabledWhenEditing: true,
      disableKeyedOptions: true,
      getIsDisabled: ({ Counterparty }) => !Counterparty,
      placeholder: '*',
    },
    { type: 'divider' },
    {
      field: 'TradeSubAccount',
      type: 'dropdown',
      guideText:
        'Subaccount used to book the dealer-side trade leg. When set, dealer trades for this rule are attributed to this subaccount.',
      getDropdownOptions: () => getSubAccountDrawerOptions(subAccounts),
    },
    {
      field: 'HedgeSubAccount',
      type: 'dropdown',
      guideText:
        'Subaccount used to book the hedge leg of a Riskless Principal trade. When set, hedge executions are attributed to this subaccount.',
      getDropdownOptions: () => getSubAccountDrawerOptions(subAccounts),
    },
  ];
}

export function getPricingRuleEntityDisplayName(
  customersByName: Map<string, Customer> | undefined,
  entity: PricingRule
): string {
  return customersByName?.get(entity.Counterparty)?.DisplayName || entity.Counterparty;
}

export const CUSTOMER_PRICING_GROUP_COLUMN_DEF: ColDef<PricingRule, string> = {
  headerName: 'Customer',
  valueFormatter: ({ context, value }) => {
    return value ? context.current.customersByName?.get(value)?.DisplayName || value : '';
  },
  sortingOrder: ['asc', 'desc'],
  comparator: (valueA, valueB, nodeA) => displayNameComparator(valueA, valueB, nodeA, ['customersByName']),
  sort: 'asc',
};

const SHORT_COLUMN_WIDTH = 100;
export const CUSTOMER_PRICING_TABLE_COLUMNS: ColumnDef<PricingRule>[] = [
  { type: 'counterparty', field: 'Counterparty', initialHide: true },
  { type: 'security', field: 'Symbol', initialHide: true },
  { type: 'bps', field: 'BidSpread' },
  { type: 'bps', field: 'OfferSpread' },
  { type: 'bps', field: 'AcceptPriceLeniency', title: 'Price Leniency' },
  { type: 'pricingAggregation', field: 'PricingAggregation' },
  { type: 'text', field: 'QuoteTTL', title: 'Quote TTL', initialWidth: SHORT_COLUMN_WIDTH },
  { type: 'text', field: 'RFQTTL', title: 'RFQ TTL', initialWidth: SHORT_COLUMN_WIDTH },
  { type: 'bps', field: 'SalesCommission', title: 'Commission', initialHide: true },
  { type: 'text', field: 'FeeMode', initialHide: true },
  { type: 'currency', field: 'FeeCurrency', initialHide: true },
  { type: 'size', field: 'Fee', params: { currencyField: 'FeeCurrency' }, initialHide: true },
  { type: 'text', field: 'PriceImprovementRatio', title: 'RFQ Price Improvement Ratio', initialHide: true },
  { type: 'text', field: 'OrderPriceImprovementRatio', title: 'Order Price Improvement Ratio', initialHide: true },
  { type: 'text', field: 'PricesTimeout', initialHide: true },
  { type: 'subAccount', field: 'HedgeSubAccount', initialHide: true },
  { type: 'subAccount', field: 'TradeSubAccount', initialHide: true },
  { type: 'text', field: 'TakeProfitInQuoteCurrency', initialHide: true },
  { type: 'text', field: 'ExcludeExchangeFees', initialHide: true },
];

export function getCustomerPricingTableDrawerOptions({
  pricingRulesByCounterpartyBySymbol,
  customers,
  customerAggregations,
  feeModes,
  currencies,
  subAccounts,
  customerSecurities,
  searchableSecurities,
  allowedCrossMarkets,
  allowedCrossCurrencies,
}: {
  pricingRulesByCounterpartyBySymbol: Map<string, Map<string | undefined, PricingRule>>;
  customers: Customer[];
  customerAggregations: Aggregation[];
  feeModes: MarketFeeModeEnum[];
  currencies: Pick<Currency, 'Symbol' | 'Description'>[];
  subAccounts: SubAccount[];
  customerSecurities: CustomerSecurity[];
  searchableSecurities: Security[];
  allowedCrossMarkets: string[];
  allowedCrossCurrencies: string[];
}): InputsAndDropdownsDrawerOption<PricingRule>[] {
  return [
    ...getCustomerPricingDrawerOptionsThroughSubAccounts({
      pricingRulesByCounterpartyBySymbol,
      customers,
      subAccounts,
      customerSecurities,
      searchableSecurities,
    }),
    { type: 'divider' },
    {
      field: 'BidSpread',
      type: 'inputNumeric',
      variant: 'bps',
      title: 'Bid Spread (BPS)',
      getIsRequired: form => !!form.OfferSpread,
    },
    {
      field: 'OfferSpread',
      type: 'inputNumeric',
      variant: 'bps',
      title: 'Offer Spread (BPS)',
      getIsRequired: form => !!form.BidSpread,
    },
    {
      field: 'AcceptPriceLeniency',
      type: 'inputNumeric',
      variant: 'bps',
      title: 'Price Leniency (BPS)',
      guideText:
        'Maximum slippage the dealer will accept when deciding whether to honor a previously quoted price, as a ratio of the customer price.',
    },
    { field: 'SalesCommission', type: 'inputNumeric', variant: 'bps', title: 'Sales Commission (BPS)' },
    { type: 'divider' },
    { field: 'FeeMode', type: 'dropdown', getDropdownOptions: () => getFeeModeDrawerOptions(feeModes) },
    {
      field: 'FeeCurrency',
      type: 'dropdown',
      getDropdownOptions: () => getCurrencyDrawerOptions(currencies),
      getIsHidden: form => form.FeeMode !== MarketFeeModeEnum.SpecificCurrency,
      getIsRequired: form => form.FeeMode === MarketFeeModeEnum.SpecificCurrency,
    },
    { field: 'Fee', type: 'inputNumeric', variant: 'bps', title: 'Fee (BPS)', getIsRequired: form => !!form.FeeMode },
    { type: 'divider' },
    {
      field: 'QuoteTTL',
      type: 'input',
      title: 'Quote TTL',
      guideText:
        'How long a dealer quote remains valid for the customer to accept. Enter as a duration string, e.g. "10s" or "30s".',
    },
    {
      field: 'RFQTTL',
      type: 'input',
      title: 'RFQ TTL',
      guideText:
        'How long an RFQ request is kept open waiting for a dealer quote before it times out. Enter as a duration string, e.g. "30s" or "1m".',
    },
    {
      field: 'AcceptTimeLeniency',
      type: 'input',
      title: 'Accept Time Leniency',
      guideText:
        'Extra time window after QuoteTTL expires during which the customer can still accept the quote. Enter as a duration string, e.g. "500ms" or "2s".',
    },
    {
      field: 'PricingAggregation',
      type: 'dropdown',
      guideText: 'Aggregation id containing list of underlying markets to use for pricing.',
      getDropdownOptions: () => getAggregationDrawerOptions(customerAggregations),
    },
    { type: 'divider' },
    {
      field: 'PriceImprovementRatio',
      type: 'input',
      title: 'RFQ Price Improvement Ratio',
      guideText:
        'If price improvement happens, this param indicates how much price improvement benefit goes to counterparty.',
    },
    {
      field: 'OrderPriceImprovementRatio',
      type: 'input',
      title: 'Order Price Improvement Ratio',
      guideText:
        'Order equivalent of RFQ Price Improvement Ratio — how much price improvement benefit goes to the counterparty on order flows.',
    },
    {
      field: 'PricesTimeout',
      type: 'input',
      guideText:
        'Maximum time in millisecond for first price update. 0 means no waiting for getting all market prices.',
    },
    { type: 'divider' },
    {
      field: 'AllowedCrossCurrencies',
      type: 'dropdown',
      title: 'Synthetic Cross Currency',
      getDropdownOptions: () => allowedCrossCurrencies.map(currency => ({ value: currency, label: currency })),
      transformControlToDrawerRecord: (value: string) => [value],
      transformDrawerRecordToControl: (value: unknown) => {
        if (Array.isArray(value) && value.length === 1) {
          return value[0];
        }
        return value;
      },
    },
    {
      field: 'AllowedCrossMarkets',
      type: 'dropdown',
      title: 'Synthetic Cross Market',
      getDropdownOptions: () => allowedCrossMarkets.map(market => ({ value: market, label: market })),
      transformControlToDrawerRecord: (value: string) => [value],
      transformDrawerRecordToControl: (value: unknown) => {
        if (Array.isArray(value) && value.length === 1) {
          return value[0];
        }
        return value;
      },
    },
    { type: 'divider' },
    {
      field: 'TakeProfitInQuoteCurrency',
      type: 'dropdown',
      getDropdownOptions: () => [
        { label: 'Enabled', value: true },
        { label: 'Disabled', value: false },
      ],
    },
    {
      field: 'ExcludeExchangeFees',
      type: 'dropdown',
      guideText: EXCLUDE_EXCHANGE_FEES_GUIDE_TEXT,
      getDropdownOptions: () => BOOLEAN_OPTIONS,
    },
  ];
}

export function mapCustomerPricingServerDataToRecord(entity: unknown): PricingRule {
  // Drop the legacy Spread field; BidSpread/OfferSpread are what EntityAdmin persists.
  const { Spread, ...pricingRule } = entity as PricingRule;

  return {
    ...pricingRule,
    BidSpread: pricingRule.BidSpread ?? Spread,
    OfferSpread: pricingRule.OfferSpread ?? Spread,
  };
}

/** [DEAL-6702] If Fee is sent without FeeMode, copy the inherited parent or default-rule FeeMode onto the PATCH. */
export function getCustomerPricingEntityForPatchPost(
  entity: PricingRule,
  {
    globalPricingRule,
    pricingRulesByCounterpartyBySymbol,
  }: {
    globalPricingRule?: Pick<PricingRule, 'FeeMode' | 'FeeCurrency'>;
    pricingRulesByCounterpartyBySymbol: Map<string, Map<string | undefined, PricingRule>>;
  }
): PricingRule {
  if (!entity.Fee || entity.FeeMode) {
    return entity;
  }

  const parentRule =
    entity.Symbol != null && entity.Counterparty
      ? pricingRulesByCounterpartyBySymbol.get(entity.Counterparty)?.get(undefined)
      : undefined;
  const feeMode = parentRule?.FeeMode ?? globalPricingRule?.FeeMode;
  if (feeMode == null) {
    return entity;
  }

  // If FeeMode is SpecificCurrency, also copy the inherited FeeCurrency
  const feeCurrency =
    feeMode === MarketFeeModeEnum.SpecificCurrency
      ? (entity.FeeCurrency ?? parentRule?.FeeCurrency ?? globalPricingRule?.FeeCurrency)
      : undefined;

  return {
    ...entity,
    FeeMode: feeMode,
    ...(feeCurrency != null ? { FeeCurrency: feeCurrency } : {}),
  };
}
